import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ajvResolver,
  SidePanelLoader,
  Popup,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { onSubmit, clearCurrencyFormState } from './store/slices/form';
import {
  useLazyGetConfigurationCurrencyQuery,
  useCreateCurrencyMutation,
} from './store/services';
import Form from '../../components/currencies/form';
import { CurrencyItem } from './types';

import { CurrenciesRoutes } from '../../routes';

const schema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Character code is required.',
      },
    },
    description: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Description is required.',
      },
    },
  },
  required: ['name', 'description'],
};

const CurrencyForm = () => {
  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CurrencyItem>({
    resolver: ajvResolver(schema),
    defaultValues: {
      active: true,
    },
  });

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const { formValues, submitSuccess } = useAppSelector(
    (state) => state.currencies.form,
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { currencyId } = useParams();
  const { search } = useLocation();

  const [getConfigurationCurrency, { isLoading }] =
    useLazyGetConfigurationCurrencyQuery();
  const [create, { isLoading: loading }] = useCreateCurrencyMutation();

  const getConfigurationCurrencyAction = useCallback(async () => {
    if (currencyId) {
      const response = (await getConfigurationCurrency(currencyId)) as {
        error?: ResponseError;
      };

      if (response?.error) {
        if (response?.error?.data?.error) {
          if (
            typeof response?.error?.data?.error === 'string' &&
            !response?.error?.data?.message
          ) {
            setGlobalError(response?.error?.data?.error);
          } else if (
            response?.error?.data?.message &&
            typeof response?.error?.data?.message === 'string'
          ) {
            setGlobalError(response?.error?.data?.message);
          } else if (
            response?.error?.data?.message &&
            Array.isArray(response?.error?.data?.message)
          ) {
            const messages: string[] = [];

            response?.error?.data?.message?.forEach((item) => {
              messages.push(item.value as string);
            });

            setGlobalError(messages?.join(', '));
          } else {
            setGlobalError('Internal Server Error. Try again.');
          }
        } else if (response?.error?.status === 500) {
          setGlobalError('Internal Server Error. Try again.');
        }
      }
    }
  }, [currencyId]);

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setPopupVisible(false);
    dispatch(clearCurrencyFormState());

    setTimeout(() => {
      navigate(`${CurrenciesRoutes.CURRENCIES_LIST}${search || ''}`);
    }, 100);
  }, [search]);

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    dispatch(onSubmit(values));

    if (!currencyId) {
      const response = (await create({
        code: Number(values.code),
        name: values.name,
        description: values.description,
        decimals: Number(values.decimals),
      })) as {
        data?: CurrencyItem;
        error?: ResponseError;
      };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      }
    }
  });

  useEffect(() => {
    if (submitSuccess) {
      handleClose();
    }
  }, [dispatch, submitSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (currencyId) {
      getConfigurationCurrencyAction();
    }
  }, [getConfigurationCurrencyAction, currencyId]);

  useEffect(() => {
    if (formValues && currencyId) {
      reset(formValues);
    }
  }, [reset, formValues, currencyId]);

  let content;

  if (isLoading) {
    content = <SidePanelLoader />;
  } else {
    content = <Form control={control} errors={errors} />;
  }

  return (
    <>
      <Popup
        title="You disable off the currency"
        description="The currency will not be available for exchange and for transactions. Disable?"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={onFormSubmit}
        cancelButtonText="Cancel"
        okButtonText={loading ? 'Loading...' : 'Yes, disable'}
        isVisible={popupVisible}
        parent={document.getElementById('currency-popup') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title="Currency Info"
        errorSidepanel={globalError}
        footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
        sidepanelExpandedKey="currency-form"
      >
        {content}
      </SidePanel>
    </>
  );
};

export default CurrencyForm;
